import {
  BUILDING_DEFS,
  CELL_SIZE,
  GRID_HEIGHT,
  GRID_WIDTH,
  RESOURCE_NODES,
  UNIT_DEFS,
  gridToWorld,
} from './definitions'
import type {
  Building,
  BuildingType,
  CargoPacket,
  CommandResult,
  GameEvent,
  GameState,
  GridCell,
  Lane,
  LaunchMode,
  Owner,
  PlayerState,
  Priority,
  ResourceKind,
  ResourceStock,
  Unit,
  UnitType,
} from './types'

const FIXED_STEP = 1 / 20
const MAX_STEPS_PER_CALL = 10_000
const RESOURCE_KINDS: ResourceKind[] = ['fossil', 'amber', 'alloy', 'genome']
const OWNERS: Owner[] = ['player', 'ai']
const PRIORITY_RANK: Record<Priority, number> = { urgent: 0, normal: 1, reserve: 2 }
const LANE_GZ: Record<Lane, number> = { north: 3, center: 5, south: 7 }
const NEIGHBORS: readonly GridCell[] = [
  { gx: -1, gz: 0 },
  { gx: 1, gz: 0 },
  { gx: 0, gz: -1 },
  { gx: 0, gz: 1 },
]

type EventInput = Omit<GameEvent, 'id' | 'time'>

interface AIBuildStep {
  type: BuildingType
  gx: number
  gz: number
  notBefore?: number
  recipe?: UnitType
  lane?: Lane
  launchMode?: LaunchMode
  priority?: Priority
}

const AI_BUILD_PLAN: readonly AIBuildStep[] = [
  { type: 'veinway', gx: 15, gz: 5 },
  { type: 'veinway', gx: 14, gz: 5 },
  { type: 'veinway', gx: 14, gz: 4 },
  { type: 'veinway', gx: 14, gz: 3 },
  { type: 'fossilRig', gx: 14, gz: 2, priority: 'urgent' },
  { type: 'veinway', gx: 14, gz: 6 },
  { type: 'veinway', gx: 14, gz: 7 },
  { type: 'amberTap', gx: 14, gz: 8, priority: 'urgent' },
  { type: 'alloyForge', gx: 13, gz: 5, priority: 'urgent' },
  { type: 'geneVat', gx: 13, gz: 6, priority: 'normal' },
  { type: 'dynamo', gx: 15, gz: 6 },
  {
    type: 'incubator',
    gx: 12,
    gz: 5,
    recipe: 'clawpack',
    lane: 'north',
    launchMode: 'pack3',
    priority: 'urgent',
  },
  { type: 'veinway', gx: 13, gz: 4 },
  { type: 'spineTurret', gx: 12, gz: 4, priority: 'urgent' },
  { type: 'dynamo', gx: 15, gz: 4 },
  {
    type: 'incubator',
    gx: 12,
    gz: 6,
    notBefore: 240,
    recipe: 'ramhorn',
    lane: 'center',
    launchMode: 'pack3',
    priority: 'normal',
  },
  { type: 'veinway', gx: 13, gz: 7 },
  { type: 'spineTurret', gx: 12, gz: 7, priority: 'urgent' },
]

function emptyStock(fossil = 0): ResourceStock {
  return { fossil, amber: 0, alloy: 0, genome: 0 }
}

function createPlayer(owner: Owner): PlayerState {
  return {
    owner,
    stock: emptyStock(80),
    powerUsed: 0,
    powerCapacity: 12,
    buildingsPlaced: 0,
    unitsSpawned: 0,
    unitsLost: 0,
    unitsDestroyed: 0,
  }
}

function normalizeSeed(seed: number | undefined): number {
  const value = (seed ?? 0xa6b3_4f21) >>> 0
  return value === 0 ? 1 : value
}

function cellKey(gx: number, gz: number): string {
  return `${gx},${gz}`
}

function otherOwner(owner: Owner): Owner {
  return owner === 'player' ? 'ai' : 'player'
}

function isAlive(building: Building): boolean {
  return !building.destroyed && building.hp > 0
}

function hasCost(stock: ResourceStock, cost: Partial<ResourceStock>): boolean {
  return RESOURCE_KINDS.every((kind) => stock[kind] + 1e-9 >= (cost[kind] ?? 0))
}

function spendCost(stock: ResourceStock, cost: Partial<ResourceStock>): void {
  for (const kind of RESOURCE_KINDS) {
    stock[kind] = Math.max(0, stock[kind] - (cost[kind] ?? 0))
  }
}

function formatMissingCost(stock: ResourceStock, cost: Partial<ResourceStock>): string {
  const missing = RESOURCE_KINDS.filter((kind) => stock[kind] + 1e-9 < (cost[kind] ?? 0))
    .map((kind) => `${Math.ceil((cost[kind] ?? 0) - stock[kind])} ${kind}`)
    .join(', ')
  return missing ? `Needs ${missing}.` : 'Insufficient resources.'
}

function distanceSquared(a: { x: number; z: number }, b: { x: number; z: number }): number {
  const dx = a.x - b.x
  const dz = a.z - b.z
  return dx * dx + dz * dz
}

function makeEvent(state: GameState, input: EventInput): GameEvent {
  return {
    id: `e${state.ids.event++}`,
    time: state.time,
    ...input,
  }
}

function makeBuilding(
  state: GameState,
  owner: Owner,
  type: BuildingType,
  gx: number,
  gz: number,
): Building {
  const definition = BUILDING_DEFS[type]
  const world = gridToWorld(gx, gz)
  return {
    id: `b${state.ids.building++}`,
    owner,
    type,
    gx,
    gz,
    ...world,
    hp: definition.maxHp,
    maxHp: definition.maxHp,
    connected: false,
    powered: false,
    paused: false,
    priority: 'normal',
    productionProgress: 0,
    isProducing: false,
    recipe: type === 'incubator' ? 'clawpack' : undefined,
    lane: 'center',
    launchMode: 'pack3',
    queue: [],
    garrison: [],
    attackCooldown: 0,
    turretShots: 0,
    createdAt: state.time,
    destroyed: false,
  }
}

export function createInitialGame(seed?: number): GameState {
  const normalizedSeed = normalizeSeed(seed)
  const state: GameState = {
    version: 1,
    seed: normalizedSeed,
    rngState: normalizedSeed,
    time: 0,
    tick: 0,
    simAccumulator: 0,
    phase: 'playing',
    players: {
      player: createPlayer('player'),
      ai: createPlayer('ai'),
    },
    resourceNodes: RESOURCE_NODES.map((node) => ({ ...node })),
    buildings: [],
    units: [],
    cargoPackets: [],
    ai: {
      planIndex: 0,
      nextDecisionAt: 2.5,
      laneCycle: 0,
    },
    ids: {
      building: 1,
      unit: 1,
      cargo: 1,
      event: 1,
    },
  }

  state.buildings.push(
    makeBuilding(state, 'player', 'heartforge', 2, 5),
    makeBuilding(state, 'ai', 'heartforge', 16, 5),
  )
  refreshNetworkAndPower(state)
  return state
}

export function restartGame(state: GameState, seed = state.seed): GameState {
  return createInitialGame(seed)
}

export function resetGame(seed?: number): GameState {
  return createInitialGame(seed)
}

export function getBuildingAt(
  state: GameState,
  gx: number,
  gz: number,
  owner?: Owner,
): Building | undefined {
  return state.buildings.find(
    (building) =>
      isAlive(building) &&
      building.gx === gx &&
      building.gz === gz &&
      (owner === undefined || building.owner === owner),
  )
}

export function getCore(state: GameState, owner: Owner): Building | undefined {
  return state.buildings.find(
    (building) => building.owner === owner && building.type === 'heartforge',
  )
}

function getResourceNodeAt(state: GameState, gx: number, gz: number) {
  return state.resourceNodes.find((node) => node.gx === gx && node.gz === gz)
}

function connectedBuildingIds(state: GameState, owner: Owner): Set<string> {
  const core = getCore(state, owner)
  if (!core || !isAlive(core)) return new Set()

  const byCell = new Map<string, Building>()
  for (const building of state.buildings) {
    if (building.owner === owner && isAlive(building)) {
      byCell.set(cellKey(building.gx, building.gz), building)
    }
  }

  const connected = new Set<string>([core.id])
  const queue: Building[] = [core]
  let cursor = 0
  while (cursor < queue.length) {
    const current = queue[cursor++]
    if (!current) continue
    for (const offset of NEIGHBORS) {
      const next = byCell.get(cellKey(current.gx + offset.gx, current.gz + offset.gz))
      if (next && !connected.has(next.id)) {
        connected.add(next.id)
        queue.push(next)
      }
    }
  }
  return connected
}

export function getNetworkPath(state: GameState, owner: Owner, buildingId: string): GridCell[] {
  const start = state.buildings.find(
    (building) => building.id === buildingId && building.owner === owner && isAlive(building),
  )
  const core = getCore(state, owner)
  if (!start || !core || !isAlive(core)) return []

  const byCell = new Map<string, Building>()
  for (const building of state.buildings) {
    if (building.owner === owner && isAlive(building)) {
      byCell.set(cellKey(building.gx, building.gz), building)
    }
  }

  const startKey = cellKey(start.gx, start.gz)
  const goalKey = cellKey(core.gx, core.gz)
  const previous = new Map<string, string | undefined>([[startKey, undefined]])
  const queue: string[] = [startKey]
  let cursor = 0

  while (cursor < queue.length && !previous.has(goalKey)) {
    const currentKey = queue[cursor++]
    if (!currentKey) continue
    const current = byCell.get(currentKey)
    if (!current) continue
    for (const offset of NEIGHBORS) {
      const nextKey = cellKey(current.gx + offset.gx, current.gz + offset.gz)
      if (byCell.has(nextKey) && !previous.has(nextKey)) {
        previous.set(nextKey, currentKey)
        queue.push(nextKey)
      }
    }
  }

  if (!previous.has(goalKey)) return []
  const reversed: GridCell[] = []
  let key: string | undefined = goalKey
  while (key !== undefined) {
    const building = byCell.get(key)
    if (!building) return []
    reversed.push({ gx: building.gx, gz: building.gz })
    key = previous.get(key)
  }
  return reversed.reverse()
}

export function refreshNetworkAndPower(state: GameState): void {
  for (const building of state.buildings) {
    building.connected = false
    building.powered = false
  }

  for (const owner of OWNERS) {
    const connected = connectedBuildingIds(state, owner)
    const active = state.buildings.filter(
      (building) => building.owner === owner && isAlive(building) && connected.has(building.id),
    )
    for (const building of active) building.connected = true

    const capacity = active.reduce(
      (total, building) => total + BUILDING_DEFS[building.type].powerOutput,
      0,
    )
    let used = 0

    for (const building of active) {
      if (BUILDING_DEFS[building.type].powerUse === 0) building.powered = true
    }

    const consumers = active
      .filter((building) => BUILDING_DEFS[building.type].powerUse > 0)
      .sort(
        (a, b) =>
          PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
          a.createdAt - b.createdAt ||
          a.id.localeCompare(b.id),
      )

    for (const building of consumers) {
      const demand = BUILDING_DEFS[building.type].powerUse
      if (used + demand <= capacity + 1e-9) {
        building.powered = true
        used += demand
      }
    }

    state.players[owner].powerCapacity = capacity
    state.players[owner].powerUsed = used
  }
}

function hasConnectedNeighbor(state: GameState, owner: Owner, gx: number, gz: number): boolean {
  const connected = connectedBuildingIds(state, owner)
  return NEIGHBORS.some((offset) => {
    const neighbor = getBuildingAt(state, gx + offset.gx, gz + offset.gz, owner)
    return neighbor !== undefined && connected.has(neighbor.id)
  })
}

export function canPlaceBuilding(
  state: GameState,
  owner: Owner,
  type: BuildingType,
  gx: number,
  gz: number,
): { ok: boolean; reason?: string } {
  if (state.phase !== 'playing') return { ok: false, reason: 'The battle is already over.' }
  if (type === 'heartforge') return { ok: false, reason: 'Heartforges can only be created at match start.' }
  if (!Number.isInteger(gx) || !Number.isInteger(gz)) {
    return { ok: false, reason: 'Buildings must snap to a grid cell.' }
  }
  if (gx < 0 || gz < 0 || gx >= GRID_WIDTH || gz >= GRID_HEIGHT) {
    return { ok: false, reason: 'That cell is outside the arena.' }
  }
  if (getBuildingAt(state, gx, gz)) return { ok: false, reason: 'That cell is occupied.' }

  const node = getResourceNodeAt(state, gx, gz)
  if (type === 'fossilRig' && node?.kind !== 'fossil') {
    return { ok: false, reason: 'Fossil Rigs require a fossil deposit.' }
  }
  if (type === 'amberTap' && node?.kind !== 'amber') {
    return { ok: false, reason: 'Amber Taps require an amber deposit.' }
  }
  if (node && type !== 'fossilRig' && type !== 'amberTap') {
    return { ok: false, reason: 'Resource deposits are reserved for matching extractors.' }
  }
  if (!hasConnectedNeighbor(state, owner, gx, gz)) {
    return { ok: false, reason: 'New buildings must touch the connected Heartforge network.' }
  }

  const definition = BUILDING_DEFS[type]
  const stock = state.players[owner].stock
  if (!hasCost(stock, definition.cost)) {
    return { ok: false, reason: formatMissingCost(stock, definition.cost) }
  }

  const connected = connectedBuildingIds(state, owner)
  let capacity = definition.powerOutput
  let demand = definition.powerUse
  for (const building of state.buildings) {
    if (building.owner !== owner || !isAlive(building) || !connected.has(building.id)) continue
    const existing = BUILDING_DEFS[building.type]
    capacity += existing.powerOutput
    demand += existing.powerUse
  }
  if (demand > capacity + 1e-9) {
    return {
      ok: false,
      reason: `Needs ${Math.ceil(demand - capacity)} more power capacity.`,
    }
  }

  return { ok: true }
}

export function placeBuilding(
  state: GameState,
  owner: Owner,
  type: BuildingType,
  gx: number,
  gz: number,
): CommandResult {
  const check = canPlaceBuilding(state, owner, type, gx, gz)
  if (!check.ok) return { ...check, events: [] }

  spendCost(state.players[owner].stock, BUILDING_DEFS[type].cost)
  const building = makeBuilding(state, owner, type, gx, gz)
  state.buildings.push(building)
  state.players[owner].buildingsPlaced += 1
  refreshNetworkAndPower(state)

  return {
    ok: true,
    events: [
      makeEvent(state, {
        type: 'build',
        action: 'placed',
        owner,
        entityId: building.id,
        buildingType: type,
        x: building.x,
        z: building.z,
      }),
    ],
  }
}

function ownedBuilding(
  state: GameState,
  owner: Owner,
  buildingId: string,
): Building | undefined {
  return state.buildings.find(
    (building) => building.id === buildingId && building.owner === owner && isAlive(building),
  )
}

function commandFailure(reason: string): CommandResult {
  return { ok: false, reason, events: [] }
}

export function sellBuilding(state: GameState, owner: Owner, buildingId: string): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type === 'heartforge') return commandFailure('The Heartforge cannot be sold.')

  const definition = BUILDING_DEFS[building.type]
  for (const kind of RESOURCE_KINDS) {
    const refund = Math.floor((definition.cost[kind] ?? 0) * 0.5)
    state.players[owner].stock[kind] += refund
  }
  building.destroyed = true
  building.hp = 0
  building.connected = false
  building.powered = false
  refreshNetworkAndPower(state)

  return {
    ok: true,
    events: [
      makeEvent(state, {
        type: 'destroy',
        action: 'sold',
        owner,
        entityId: building.id,
        buildingType: building.type,
        x: building.x,
        z: building.z,
      }),
    ],
  }
}

export function setBuildingRecipe(
  state: GameState,
  owner: Owner,
  buildingId: string,
  unitType: UnitType,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type !== 'incubator') return commandFailure('Only Incubators grow units.')
  building.recipe = unitType
  return { ok: true, events: [] }
}

export function setBuildingLane(
  state: GameState,
  owner: Owner,
  buildingId: string,
  lane: Lane,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type !== 'incubator') return commandFailure('Only Incubators have a rally lane.')
  building.lane = lane
  return { ok: true, events: [] }
}

export function setLaunchMode(
  state: GameState,
  owner: Owner,
  buildingId: string,
  launchMode: LaunchMode,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type !== 'incubator') return commandFailure('Only Incubators have launch modes.')
  building.launchMode = launchMode
  return { ok: true, events: [] }
}

export function setPriority(
  state: GameState,
  owner: Owner,
  buildingId: string,
  priority: Priority,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  building.priority = priority
  refreshNetworkAndPower(state)
  return { ok: true, events: [] }
}

export function toggleBuildingPaused(
  state: GameState,
  owner: Owner,
  buildingId: string,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type === 'heartforge' || building.type === 'veinway' || building.type === 'dynamo') {
    return commandFailure('That building has no pausable process.')
  }
  building.paused = !building.paused
  return { ok: true, events: [] }
}

export function launchIncubator(
  state: GameState,
  owner: Owner,
  buildingId: string,
): CommandResult {
  const building = ownedBuilding(state, owner, buildingId)
  if (!building) return commandFailure('Building not found.')
  if (building.type !== 'incubator') return commandFailure('Only Incubators can launch units.')
  if (building.garrison.length === 0) return commandFailure('The Incubator garrison is empty.')
  if (state.phase !== 'playing') return commandFailure('The battle is already over.')

  const events: GameEvent[] = []
  spawnGarrison(state, building, events)
  return { ok: true, events }
}

function spawnGarrison(state: GameState, building: Building, events: GameEvent[]): void {
  const core = getCore(state, building.owner)
  if (!core || !isAlive(core)) return

  const direction = building.owner === 'player' ? 1 : -1
  const types = building.garrison.splice(0)
  types.forEach((type, index) => {
    const laneGz = LANE_GZ[building.lane]
    const base = gridToWorld(core.gx + direction * (0.78 + index * 0.18), laneGz)
    const rowOffset = ((index % 3) - 1) * 0.22
    const definition = UNIT_DEFS[type]
    const unit: Unit = {
      id: `u${state.ids.unit++}`,
      owner: building.owner,
      type,
      lane: building.lane,
      x: base.x - direction * Math.floor(index / 3) * 0.28,
      z: base.z + rowOffset,
      hp: definition.maxHp,
      maxHp: definition.maxHp,
      attackCooldown: 0,
      spawnedAt: state.time,
    }
    state.units.push(unit)
    state.players[building.owner].unitsSpawned += 1
    events.push(
      makeEvent(state, {
        type: 'spawn',
        action: 'unit',
        owner: building.owner,
        entityId: unit.id,
        sourceId: building.id,
        unitType: type,
        lane: building.lane,
        x: unit.x,
        z: unit.z,
      }),
    )
  })
}

function spawnCargo(
  state: GameState,
  building: Building,
  kind: ResourceKind,
  amount: number,
  events: GameEvent[],
): void {
  const path = getNetworkPath(state, building.owner, building.id)
  if (path.length === 0) return
  const start = gridToWorld(path[0]?.gx ?? building.gx, path[0]?.gz ?? building.gz)
  const packet: CargoPacket = {
    id: `c${state.ids.cargo++}`,
    owner: building.owner,
    kind,
    amount,
    sourceBuildingId: building.id,
    path,
    pathIndex: 0,
    segmentProgress: 0,
    speed: 4.5,
    ...start,
  }
  state.cargoPackets.push(packet)
  events.push(
    makeEvent(state, {
      type: 'cargo',
      action: 'dispatched',
      owner: building.owner,
      entityId: packet.id,
      sourceId: building.id,
      resource: kind,
      amount,
      x: packet.x,
      z: packet.z,
    }),
  )
}

function updateCargo(state: GameState, dt: number, events: GameEvent[]): void {
  const delivered = new Set<string>()
  for (const packet of state.cargoPackets) {
    let remaining = packet.speed * dt
    while (remaining > 1e-9 && packet.pathIndex < packet.path.length - 1) {
      const needed = 1 - packet.segmentProgress
      const travelled = Math.min(needed, remaining)
      packet.segmentProgress += travelled
      remaining -= travelled
      if (packet.segmentProgress >= 1 - 1e-9) {
        packet.pathIndex += 1
        packet.segmentProgress = 0
      }
    }

    const current = packet.path[packet.pathIndex]
    const next = packet.path[Math.min(packet.pathIndex + 1, packet.path.length - 1)]
    if (current && next) {
      const from = gridToWorld(current.gx, current.gz)
      const to = gridToWorld(next.gx, next.gz)
      packet.x = from.x + (to.x - from.x) * packet.segmentProgress
      packet.z = from.z + (to.z - from.z) * packet.segmentProgress
    }

    if (packet.pathIndex >= packet.path.length - 1) {
      state.players[packet.owner].stock[packet.kind] += packet.amount
      delivered.add(packet.id)
      events.push(
        makeEvent(state, {
          type: 'cargo',
          action: 'delivered',
          owner: packet.owner,
          entityId: packet.id,
          targetId: getCore(state, packet.owner)?.id,
          resource: packet.kind,
          amount: packet.amount,
          x: packet.x,
          z: packet.z,
        }),
      )
    }
  }
  if (delivered.size > 0) {
    state.cargoPackets = state.cargoPackets.filter((packet) => !delivered.has(packet.id))
  }
}

function isOperational(building: Building): boolean {
  return isAlive(building) && building.connected && building.powered && !building.paused
}

function updateExtractors(state: GameState, dt: number, events: GameEvent[]): void {
  for (const building of state.buildings) {
    if (
      !isOperational(building) ||
      (building.type !== 'fossilRig' && building.type !== 'amberTap')
    ) {
      continue
    }
    const duration = BUILDING_DEFS[building.type].productionTime ?? 1.4
    building.productionProgress += dt
    while (building.productionProgress + 1e-9 >= duration) {
      building.productionProgress -= duration
      const node = getResourceNodeAt(state, building.gx, building.gz)
      const kind: ResourceKind = building.type === 'fossilRig' ? 'fossil' : 'amber'
      spawnCargo(state, building, kind, node?.richness ?? 1, events)
    }
  }
}

function updateProcessor(
  state: GameState,
  building: Building,
  dt: number,
  events: GameEvent[],
): void {
  const input: ResourceKind = building.type === 'alloyForge' ? 'fossil' : 'amber'
  const output: ResourceKind = building.type === 'alloyForge' ? 'alloy' : 'genome'
  const stock = state.players[building.owner].stock
  const duration = BUILDING_DEFS[building.type].productionTime ?? 3

  if (!building.isProducing && stock[input] + 1e-9 >= 2) {
    stock[input] = Math.max(0, stock[input] - 2)
    building.isProducing = true
    building.productionProgress = 0
  }
  if (!building.isProducing) return

  building.productionProgress += dt
  if (building.productionProgress + 1e-9 >= duration) {
    building.productionProgress = 0
    building.isProducing = false
    spawnCargo(state, building, output, 1, events)
    events.push(
      makeEvent(state, {
        type: 'production',
        action: 'refined',
        owner: building.owner,
        entityId: building.id,
        resource: output,
        amount: 1,
        x: building.x,
        z: building.z,
      }),
    )
  }
}

function launchThreshold(mode: LaunchMode): number {
  if (mode === 'drip') return 1
  if (mode === 'pack3') return 3
  if (mode === 'swarm6') return 6
  return Number.POSITIVE_INFINITY
}

function updateIncubator(
  state: GameState,
  building: Building,
  dt: number,
  events: GameEvent[],
): void {
  const stock = state.players[building.owner].stock
  const recipe = building.recipe ?? 'clawpack'
  if (building.queue.length === 0 && building.garrison.length < 12) {
    const cost = UNIT_DEFS[recipe].cost
    if (hasCost(stock, cost)) {
      spendCost(stock, cost)
      building.queue.push(recipe)
      building.productionProgress = 0
      building.isProducing = true
    }
  }

  const activeRecipe = building.queue[0]
  if (activeRecipe) {
    building.productionProgress += dt
    if (building.productionProgress + 1e-9 >= UNIT_DEFS[activeRecipe].buildTime) {
      building.queue.shift()
      building.garrison.push(activeRecipe)
      building.productionProgress = 0
      building.isProducing = false
      events.push(
        makeEvent(state, {
          type: 'production',
          action: 'unit-ready',
          owner: building.owner,
          entityId: building.id,
          unitType: activeRecipe,
          lane: building.lane,
          x: building.x,
          z: building.z,
        }),
      )
    }
  }

  if (building.garrison.length >= launchThreshold(building.launchMode)) {
    spawnGarrison(state, building, events)
  }
}

function updateProduction(state: GameState, dt: number, events: GameEvent[]): void {
  updateExtractors(state, dt, events)
  const consumers = state.buildings
    .filter(
      (building) =>
        isOperational(building) &&
        (building.type === 'alloyForge' ||
          building.type === 'geneVat' ||
          building.type === 'incubator'),
    )
    .sort(
      (a, b) =>
        PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
        a.createdAt - b.createdAt ||
        a.id.localeCompare(b.id),
    )

  for (const building of consumers) {
    if (building.type === 'incubator') updateIncubator(state, building, dt, events)
    else updateProcessor(state, building, dt, events)
  }
}

function configureAIPlacement(state: GameState, step: AIBuildStep): void {
  const building = getBuildingAt(state, step.gx, step.gz, 'ai')
  if (!building) return
  if (step.priority) setPriority(state, 'ai', building.id, step.priority)
  if (step.recipe) setBuildingRecipe(state, 'ai', building.id, step.recipe)
  if (step.lane) setBuildingLane(state, 'ai', building.id, step.lane)
  if (step.launchMode) setLaunchMode(state, 'ai', building.id, step.launchMode)
}

function updateAI(state: GameState, events: GameEvent[]): void {
  if (state.time + 1e-9 < state.ai.nextDecisionAt) return
  state.ai.nextDecisionAt += 0.9

  const step = AI_BUILD_PLAN[state.ai.planIndex]
  const aiBuildings = state.buildings.filter((building) => building.owner === 'ai' && isAlive(building))
  if (!step) {
    for (const building of aiBuildings) {
      if (building.type === 'incubator' || building.type === 'alloyForge' || building.type === 'geneVat') {
        building.paused = false
      }
    }
    return
  }
  if (step.notBefore !== undefined && state.time < step.notBefore) return

  const stock = state.players.ai.stock
  const cost = BUILDING_DEFS[step.type].cost
  const canAffordNext = hasCost(stock, cost)
  for (const building of aiBuildings) {
    if (building.type === 'incubator') building.paused = !canAffordNext
    if (building.type === 'alloyForge') {
      const alloyReady = stock.alloy + 1e-9 >= (cost.alloy ?? 0)
      const fossilMissing = stock.fossil + 1e-9 < (cost.fossil ?? 0)
      building.paused = alloyReady && fossilMissing
    }
    if (building.type === 'geneVat') {
      const genomeReady = stock.genome + 1e-9 >= (cost.genome ?? 0)
      building.paused = genomeReady
    }
  }

  const result = placeBuilding(state, 'ai', step.type, step.gx, step.gz)
  if (!result.ok) return
  events.push(...result.events)
  configureAIPlacement(state, step)
  state.ai.planIndex += 1
}

function applyUnitDamage(
  state: GameState,
  target: Unit,
  damage: number,
  sourceOwner: Owner,
  sourceId: string,
  events: GameEvent[],
): void {
  if (target.hp <= 0) return
  target.hp = Math.max(0, target.hp - damage)
  events.push(
    makeEvent(state, {
      type: 'hit',
      action: 'unit',
      owner: sourceOwner,
      sourceId,
      targetId: target.id,
      unitType: target.type,
      amount: damage,
      x: target.x,
      z: target.z,
    }),
  )
  if (target.hp <= 0) {
    state.players[target.owner].unitsLost += 1
    state.players[sourceOwner].unitsDestroyed += 1
    events.push(
      makeEvent(state, {
        type: 'destroy',
        action: 'unit',
        owner: target.owner,
        sourceId,
        targetId: target.id,
        unitType: target.type,
        x: target.x,
        z: target.z,
      }),
    )
  }
}

function applyBuildingDamage(
  state: GameState,
  target: Building,
  damage: number,
  sourceOwner: Owner,
  sourceId: string,
  events: GameEvent[],
): void {
  if (!isAlive(target)) return
  target.hp = Math.max(0, target.hp - damage)
  events.push(
    makeEvent(state, {
      type: 'hit',
      action: 'building',
      owner: sourceOwner,
      sourceId,
      targetId: target.id,
      buildingType: target.type,
      amount: damage,
      x: target.x,
      z: target.z,
    }),
  )
  if (target.hp > 0) return

  target.destroyed = true
  target.connected = false
  target.powered = false
  events.push(
    makeEvent(state, {
      type: 'destroy',
      action: 'building',
      owner: target.owner,
      sourceId,
      targetId: target.id,
      buildingType: target.type,
      x: target.x,
      z: target.z,
    }),
  )

  if (target.type === 'heartforge') {
    state.winner = sourceOwner
    state.phase = sourceOwner === 'player' ? 'won' : 'lost'
    events.push(
      makeEvent(state, {
        type: 'win',
        action: state.phase,
        owner: sourceOwner,
        sourceId,
        targetId: target.id,
        x: target.x,
        z: target.z,
        message: sourceOwner === 'player' ? 'Enemy Heartforge shattered.' : 'Your Heartforge fell.',
      }),
    )
  }
}

function updateTurrets(state: GameState, dt: number, events: GameEvent[]): void {
  const turrets = state.buildings
    .filter((building) => building.type === 'spineTurret' && isAlive(building))
    .sort((a, b) => a.id.localeCompare(b.id))

  for (const turret of turrets) {
    turret.attackCooldown = Math.max(0, turret.attackCooldown - dt)
    if (!isOperational(turret) || turret.attackCooldown > 0) continue

    const rangeSq = (CELL_SIZE * 3.6) ** 2
    const targets = state.units
      .filter((unit) => unit.owner !== turret.owner && unit.hp > 0 && distanceSquared(unit, turret) <= rangeSq)
      .sort(
        (a, b) => distanceSquared(a, turret) - distanceSquared(b, turret) || a.id.localeCompare(b.id),
      )
    const target = targets[0]
    if (!target) continue

    if (turret.turretShots >= 9) {
      if (state.players[turret.owner].stock.alloy < 1) continue
      state.players[turret.owner].stock.alloy -= 1
      turret.turretShots = 0
    } else {
      turret.turretShots += 1
    }

    turret.attackCooldown = 0.72
    events.push(
      makeEvent(state, {
        type: 'attack',
        action: 'turret',
        owner: turret.owner,
        sourceId: turret.id,
        targetId: target.id,
        x: turret.x,
        z: turret.z,
      }),
    )
    applyUnitDamage(state, target, 11, turret.owner, turret.id, events)
  }
}

function nearestUnitTarget(state: GameState, unit: Unit): Unit | undefined {
  const definition = UNIT_DEFS[unit.type]
  const rangeSq = definition.aggroRange ** 2
  return state.units
    .filter(
      (candidate) =>
        candidate.owner !== unit.owner &&
        candidate.hp > 0 &&
        Math.abs(candidate.z - unit.z) <= CELL_SIZE * 1.5 &&
        distanceSquared(candidate, unit) <= rangeSq,
    )
    .sort((a, b) => distanceSquared(a, unit) - distanceSquared(b, unit) || a.id.localeCompare(b.id))[0]
}

function nearestBuildingTarget(state: GameState, unit: Unit): Building | undefined {
  const definition = UNIT_DEFS[unit.type]
  const rangeSq = definition.aggroRange ** 2
  const nearby = state.buildings.filter(
    (building) =>
      building.owner !== unit.owner && isAlive(building) && distanceSquared(building, unit) <= rangeSq,
  )
  nearby.sort((a, b) => {
    const aTurret = a.type === 'spineTurret' ? 0 : 1
    const bTurret = b.type === 'spineTurret' ? 0 : 1
    return aTurret - bTurret || distanceSquared(a, unit) - distanceSquared(b, unit) || a.id.localeCompare(b.id)
  })
  return nearby[0]
}

function moveTowards(unit: Unit, target: { x: number; z: number }, distance: number): void {
  const dx = target.x - unit.x
  const dz = target.z - unit.z
  const length = Math.hypot(dx, dz)
  if (length <= 1e-9) return
  const step = Math.min(distance, length)
  unit.x += (dx / length) * step
  unit.z += (dz / length) * step
}

function updateUnits(state: GameState, dt: number, events: GameEvent[]): void {
  const units = [...state.units].sort((a, b) => a.id.localeCompare(b.id))
  for (const unit of units) {
    if (unit.hp <= 0 || state.phase !== 'playing') continue
    const definition = UNIT_DEFS[unit.type]
    unit.attackCooldown = Math.max(0, unit.attackCooldown - dt)

    const unitTarget = nearestUnitTarget(state, unit)
    const buildingTarget = unitTarget ? undefined : nearestBuildingTarget(state, unit)
    const coreTarget = buildingTarget ? undefined : getCore(state, otherOwner(unit.owner))
    const target = unitTarget ?? buildingTarget ?? coreTarget
    if (!target || ('destroyed' in target && !isAlive(target))) continue
    unit.targetId = target.id

    const targetRadius = 'gx' in target ? CELL_SIZE * 0.48 : 0.32
    const attackDistance = definition.attackRange + targetRadius
    const distanceSq = distanceSquared(unit, target)

    if (distanceSq <= attackDistance * attackDistance) {
      if (unit.attackCooldown > 0) continue
      unit.attackCooldown = definition.attackCooldown
      events.push(
        makeEvent(state, {
          type: 'attack',
          action: unit.type,
          owner: unit.owner,
          sourceId: unit.id,
          targetId: target.id,
          unitType: unit.type,
          x: unit.x,
          z: unit.z,
        }),
      )

      if ('gx' in target) {
        applyBuildingDamage(
          state,
          target,
          definition.damage * definition.buildingMultiplier,
          unit.owner,
          unit.id,
          events,
        )
      } else {
        applyUnitDamage(state, target, definition.damage, unit.owner, unit.id, events)
        if (definition.splashRadius > 0) {
          const splashSq = definition.splashRadius ** 2
          for (const splashTarget of state.units) {
            if (
              splashTarget.id !== target.id &&
              splashTarget.owner !== unit.owner &&
              splashTarget.hp > 0 &&
              distanceSquared(splashTarget, target) <= splashSq
            ) {
              applyUnitDamage(
                state,
                splashTarget,
                definition.damage * 0.35,
                unit.owner,
                unit.id,
                events,
              )
            }
          }
        }
      }
      continue
    }

    if (unitTarget || buildingTarget) {
      moveTowards(unit, target, definition.speed * dt)
    } else {
      const lanePoint = gridToWorld(unit.owner === 'player' ? GRID_WIDTH - 2 : 1, LANE_GZ[unit.lane])
      moveTowards(unit, lanePoint, definition.speed * dt)
    }
  }

  state.units = state.units.filter((unit) => unit.hp > 0)
}

function simulateStep(state: GameState, events: GameEvent[]): void {
  state.tick += 1
  state.time = state.tick * FIXED_STEP
  refreshNetworkAndPower(state)
  updateAI(state, events)
  refreshNetworkAndPower(state)
  updateProduction(state, FIXED_STEP, events)
  updateCargo(state, FIXED_STEP, events)
  updateTurrets(state, FIXED_STEP, events)
  updateUnits(state, FIXED_STEP, events)
  refreshNetworkAndPower(state)
}

export function tickGame(state: GameState, dt: number): GameEvent[] {
  const events: GameEvent[] = []
  if (state.phase !== 'playing' || !Number.isFinite(dt) || dt <= 0) return events

  state.simAccumulator += dt
  let steps = 0
  while (state.simAccumulator + 1e-9 >= FIXED_STEP && steps < MAX_STEPS_PER_CALL) {
    state.simAccumulator -= FIXED_STEP
    if (state.simAccumulator < 0) state.simAccumulator = 0
    simulateStep(state, events)
    steps += 1
    if (state.phase !== 'playing') {
      state.simAccumulator = 0
      break
    }
  }
  if (Math.abs(state.simAccumulator) < 1e-9) state.simAccumulator = 0
  return events
}
